import { Controller } from "@hotwired/stimulus"

// Learner timeline block (TMS-912): each stage on the track reveals its panel
// under it. Only one stage is open at a time (TMS-1617) — opening a stage closes
// the one that was open, and picking the open stage closes it again, so the
// learner can still collapse the whole timeline.
//
// Every stage is its own button in the tab order. Left/Right, Home and End only
// move focus along the track; opening is left to Enter/Space, otherwise walking
// the track would open every stage it passed. Up/Down are left to the browser so
// they still scroll the page from a focused stage.
export default class extends Controller {
  static targets = ["stage", "panel"]

  toggleStage(event) {
    const stage = event.currentTarget
    const open = stage.getAttribute("aria-expanded") !== "true"

    if (open) this.closeOtherStages(stage)
    this.setStageOpen(stage, open)
  }

  // A review deep link can land on a comment left on a stage's panel, which is
  // hidden until its stage is opened — the review canvas asks for it to be shown
  // before scrolling to it.
  revealPanel(event) {
    const panel = this.panelTargets.find((candidate) => candidate.contains(event.target))
    const stage = panel && this.stageFor(Number(panel.dataset.stageIndex))
    if (!stage) return

    this.closeOtherStages(stage)
    this.setStageOpen(stage, true)
  }

  closeOtherStages(stage) {
    this.stageTargets.forEach((other) => {
      if (other !== stage) this.setStageOpen(other, false)
    })
  }

  setStageOpen(stage, open) {
    stage.setAttribute("aria-expanded", open ? "true" : "false")
    stage.classList.toggle("is-open", open)

    const panel = this.panelFor(Number(stage.dataset.stageIndex))
    if (panel) panel.hidden = !open
  }

  moveFocusOnArrowKeys(event) {
    const step = this.arrowKeyStep(event.key)
    if (step === null) return

    event.preventDefault()

    const currentIndex = Number(event.currentTarget.dataset.stageIndex)
    const count = this.stageTargets.length
    const nextIndex = this.nextStageIndex(event.key, currentIndex, step, count)

    this.stageFor(nextIndex)?.focus()
  }

  arrowKeyStep(key) {
    if (key === "ArrowRight") return 1
    if (key === "ArrowLeft") return -1
    if (key === "Home" || key === "End") return 0

    return null
  }

  nextStageIndex(key, currentIndex, step, count) {
    if (key === "Home") return 0
    if (key === "End") return count - 1

    return (currentIndex + step + count) % count
  }

  stageFor(index) {
    return this.stageTargets.find((stage) => Number(stage.dataset.stageIndex) === index)
  }

  panelFor(index) {
    return this.panelTargets.find((panel) => Number(panel.dataset.stageIndex) === index)
  }
}
